/**
 * ScreenRouter - Routes and renders the appropriate screen based on current state
 *
 * This component extracts the screen rendering logic from the main modal component,
 * making the code more maintainable and testable. It handles all 10 screen cases:
 * - confirmation, booking, phone, verify, details
 * - email-conflict, account-linked, payment-existing, payment-new
 * - login-email
 */
import arrayMutators from 'final-form-arrays';
import React from 'react';
import { Form } from 'react-final-form';

import {
  AccountLinkedStep,
  ConfirmationScreen,
  EmailConflictStep,
  ExpertHeader,
  LoginForm,
  OTPVerificationStep,
  PaymentStep,
  PhoneEntryStep,
  SchedulingStep,
  Stepper,
} from '../';
import { OTP_STEPS } from '../../constants';
import type { ScreenRouterProps } from '../../types';
import { validateSchedulingForm } from '../../validation';
import { DetailsScreenContent } from './DetailsScreenContent';

export const ScreenRouter: React.FC<ScreenRouterProps> = ({
  // Current screen
  screen,

  // Expert/consultation info
  expert,
  hourlyRate,
  landingUrl,
  authenticatedViewer,

  // OTP flow state
  otpFlow,

  // Signup flow state
  signupFlow,

  // Email login state
  emailLogin,

  // Local state
  isAuthenticatedUserMode,

  // Analytics
  modalOpenTime,

  // Callbacks
  onClose,
  onComplete,
  onContinue,
  onSchedulingContinue,
}) => {
  // Show confirmation screen
  if (screen === 'confirmation') {
    // New signups ('new' or 'pending-resuming') should redirect to dashboard
    // Existing users ('existing') should stay on current page
    const isNewSignup = otpFlow.userData?.type !== 'existing';

    return (
      <>
        <ExpertHeader expert={expert} />
        <ConfirmationScreen
          onComplete={onComplete}
          onContinue={onContinue}
          expert={expert}
          isNewSignup={isNewSignup}
        />
      </>
    );
  }

  // Show scheduling step
  if (screen === 'booking') {
    const viewerForScheduling = authenticatedViewer?.timezone
      ? { timezone: authenticatedViewer.timezone }
      : signupFlow.viewer;

    return (
      <>
        <ExpertHeader expert={expert} />
        <Form
          key="scheduling-form"
          initialValues={signupFlow.schedulingInitialValues}
          onSubmit={onSchedulingContinue}
          validate={validateSchedulingForm}
          mutators={{ ...arrayMutators }}
        >
          {({ handleSubmit: formHandleSubmit, valid }) => (
            <SchedulingStep
              expert={expert}
              viewer={viewerForScheduling as any}
              onContinue={formHandleSubmit}
              onCancel={onClose}
              valid={valid}
            />
          )}
        </Form>
      </>
    );
  }

  // Show phone entry step
  if (screen === 'phone') {
    return (
      <>
        <ExpertHeader expert={expert} />
        <Stepper currentStep={0} steps={OTP_STEPS.NEW_USER} />
        <PhoneEntryStep
          expert={expert}
          duration={signupFlow.selectedDuration}
          hourlyRate={hourlyRate}
          onSendCode={otpFlow.handleSendCode}
          onBack={() => otpFlow.goToScreen('booking')}
          onLoginWithEmail={() => otpFlow.goToScreen('login-email')}
          isLoading={otpFlow.isLoading}
          error={otpFlow.error || undefined}
          initialPhone={otpFlow.verifiedPhone}
        />
      </>
    );
  }

  // Show OTP verification step
  if (screen === 'verify') {
    const steps = otpFlow.isReturningUser ? OTP_STEPS.RETURNING_USER : OTP_STEPS.NEW_USER;
    return (
      <>
        <ExpertHeader expert={expert} />
        <Stepper currentStep={0} steps={steps} />
        <OTPVerificationStep
          isReturningUser={otpFlow.isReturningUser}
          userName={otpFlow.userData?.name}
          userCompany={otpFlow.userData?.company}
          maskedAddress={otpFlow.maskedAddress}
          transport={otpFlow.transport}
          expert={expert}
          duration={signupFlow.selectedDuration}
          hourlyRate={hourlyRate}
          onVerify={otpFlow.handleVerifyCode}
          onResend={otpFlow.handleResendCode}
          onUseAnotherAccount={otpFlow.handleUseAnotherAccount}
          onBack={() => otpFlow.goToScreen('phone')}
          isLoading={otpFlow.isLoading}
          error={otpFlow.error || undefined}
        />
      </>
    );
  }

  // Show user details form
  if (screen === 'details') {
    return (
      <DetailsScreenContent
        expert={expert}
        userData={otpFlow.userData}
        profileState={otpFlow.profileState}
        isAuthenticatedUserMode={isAuthenticatedUserMode}
        otpVerificationToken={otpFlow.otpVerificationToken}
        signupFormData={otpFlow.signupFormData}
        isLoading={otpFlow.isLoading}
        error={otpFlow.error}
        missingFields={otpFlow.missingFields}
        partialProfileData={otpFlow.partialProfileData}
        isExistingUserFromEmailLogin={otpFlow.isExistingUserFromEmailLogin}
        billingAccount={otpFlow.billingAccount}
        onNavigate={otpFlow.goToScreen}
        onSetStripeState={otpFlow.setStripeState}
        onSetSignupFormData={otpFlow.setSignupFormData}
        onSubmitNewUser={otpFlow.handleDetailsSubmit}
      />
    );
  }

  // Show email conflict step
  if (screen === 'email-conflict') {
    return (
      <>
        <ExpertHeader expert={expert} />
        <EmailConflictStep
          email={otpFlow.conflictEmail || ''}
          phone={otpFlow.verifiedPhone}
          onLinkSuccess={otpFlow.handleLinkSuccess}
          onUseDifferentEmail={otpFlow.handleUseDifferentEmail}
          onForgotPassword={() => window.open('/reset-password', '_blank')}
          isLoading={otpFlow.isLoading}
          error={otpFlow.error || undefined}
        />
      </>
    );
  }

  // Show account linked step
  if (screen === 'account-linked') {
    return (
      <>
        <ExpertHeader expert={expert} />
        <AccountLinkedStep
          expert={expert}
          duration={signupFlow.selectedDuration}
          hourlyRate={hourlyRate}
          onContinue={() => otpFlow.goToScreen('payment-existing')}
        />
      </>
    );
  }

  // Show payment step for returning/active users
  if (screen === 'payment-existing') {
    return (
      <>
        <ExpertHeader expert={expert} />
        <PaymentStep
          clientSecret={null}
          stripeCustomerId={null}
          isProcessing={signupFlow.isProcessingPayment}
          setIsProcessing={signupFlow.setIsProcessingPayment}
          paymentProgress={signupFlow.paymentProgress}
          onPaymentProgressChange={signupFlow.updatePaymentProgress}
          // onError writes to otpFlow.error, so read that back. Reading
          // signupFlow.paymentError meant every payment error on these screens
          // (declines, expired cards, Stripe load failures) rendered nowhere:
          // nothing routes the form's onError into signupFlow.
          paymentError={otpFlow.error}
          onSuccess={otpFlow.handlePaymentSuccess}
          onError={otpFlow.handlePaymentError}
          onBack={() => otpFlow.goToScreen('booking')}
          expert={expert}
          duration={signupFlow.selectedDuration}
          schedulingData={signupFlow.schedulingData}
          landingUrl={landingUrl}
          isReturningUser={true}
          userName={otpFlow.userData?.name}
          userCompany={otpFlow.userData?.company}
          billingAccount={otpFlow.billingAccount || undefined}
          groupId={otpFlow.userData?.groupId}
          userId={otpFlow.userData?.id}
          modalOpenTime={modalOpenTime}
        />
      </>
    );
  }

  // Show payment step for new users OR existing users without billing
  if (screen === 'payment-new') {
    const isExistingUserNeedingBilling = otpFlow.isExistingUserWithoutBilling;
    const isPendingUserResuming = otpFlow.userData?.type === 'pending-resuming';
    const isResuming = isPendingUserResuming || isExistingUserNeedingBilling;
    const steps = isResuming ? OTP_STEPS.RETURNING_USER : OTP_STEPS.NEW_USER;
    const stepIndex = isResuming ? 1 : 2;
    const userName = isResuming ? otpFlow.userData?.name : undefined;
    const userCompany = isResuming ? otpFlow.userData?.company : undefined;

    return (
      <>
        <ExpertHeader expert={expert} />
        <Stepper currentStep={stepIndex} steps={steps} />
        <PaymentStep
          clientSecret={otpFlow.clientSecret}
          stripeCustomerId={otpFlow.stripeCustomerId}
          isProcessing={signupFlow.isProcessingPayment}
          setIsProcessing={signupFlow.setIsProcessingPayment}
          paymentProgress={signupFlow.paymentProgress}
          onPaymentProgressChange={signupFlow.updatePaymentProgress}
          // onError writes to otpFlow.error, so read that back. Reading
          // signupFlow.paymentError meant every payment error on these screens
          // (declines, expired cards, Stripe load failures) rendered nowhere:
          // nothing routes the form's onError into signupFlow.
          paymentError={otpFlow.error}
          onSuccess={otpFlow.handlePaymentSuccess}
          onError={otpFlow.handlePaymentError}
          onBack={() => otpFlow.goToScreen('booking')}
          expert={expert}
          duration={signupFlow.selectedDuration}
          schedulingData={signupFlow.schedulingData}
          landingUrl={landingUrl}
          isExistingUserWithoutBilling={isExistingUserNeedingBilling}
          isReturningUser={isResuming}
          userName={userName}
          userCompany={userCompany}
          otpVerificationToken={otpFlow.otpVerificationToken}
          userId={otpFlow.userData?.id}
          modalOpenTime={modalOpenTime}
        />
      </>
    );
  }

  // Show email login fallback
  if (screen === 'login-email') {
    return (
      <>
        <ExpertHeader expert={expert} />
        <LoginForm
          loginEmail={emailLogin.email}
          setLoginEmail={emailLogin.setEmail}
          loginPassword={emailLogin.password}
          setLoginPassword={emailLogin.setPassword}
          loginError={emailLogin.error}
          isLoggingIn={emailLogin.isLoading}
          onLogin={emailLogin.handleLogin}
          onBack={() => otpFlow.goToScreen('phone')}
          // MFA props
          mfa={emailLogin.mfa}
          otpCode={emailLogin.otpCode}
          setOtpCode={emailLogin.setOtpCode}
          onMfaVerify={emailLogin.handleMfaVerify}
          onMfaCancel={emailLogin.handleMfaCancel}
          onMfaResend={emailLogin.handleMfaResend}
          mfaResendSuccess={emailLogin.mfaResendSuccess}
        />
      </>
    );
  }

  // Default fallback
  return null;
};
